import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  BarChart3,
  MessageSquare,
  Users,
  TrendingUp,
  Target,
  Zap,
  RefreshCw,
  AlertCircle,
  Construction,
  Loader2,
  MessageCircle,
  Bot
} from 'lucide-react';
import { api } from '../../services/api';

export default function Analytics() {
  const { id } = useParams();
  const [timeRange, setTimeRange] = useState('30d');
  const [analytics, setAnalytics] = useState(null);
  const [agent, setAgent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [analyticsRes, agentRes] = await Promise.all([
        api.getAnalytics(id, timeRange),
        api.getAgent(id)
      ]);
      setAnalytics(analyticsRes.analytics);
      setAgent(agentRes.agent);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [id, timeRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (isLoading && !analytics) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (error && !analytics) {
    return (
      <div className="p-8">
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
          <button
            onClick={fetchAnalytics}
            className="ml-auto text-sm text-red-600 hover:text-red-800 underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Use data from API or defaults
  const totalConversations = analytics?.total_conversations || 0;
  const totalLeads = analytics?.total_leads || 0;
  const totalMessages = analytics?.total_messages || 0;
  const messagesToday = analytics?.messages_today || 0;
  const inputMessages = analytics?.input_messages || 0;
  const outputMessages = analytics?.output_messages || 0;
  const conversationsByDay = analytics?.conversations_by_day || [];
  const leadsByDay = analytics?.leads_by_day || [];
  const messagesByDay = analytics?.messages_by_day || [];

  // Use range-specific data from API
  const conversationsInRange = analytics?.conversations_in_range || 0;
  const leadsInRange = analytics?.leads_in_range || 0;

  // Calculate derived metrics based on selected range
  const conversionRate = conversationsInRange > 0 ? Math.round((leadsInRange / conversationsInRange) * 100) : 0;

  // Time range labels
  const rangeLabels = {
    '24h': 'Today',
    '7d': 'This Week',
    '30d': 'This Month',
    '90d': 'Last 90 Days'
  };
  const rangePeriod = rangeLabels[timeRange] || timeRange;

  const metrics = [
    {
      label: 'Conversations',
      value: conversationsInRange,
      icon: MessageSquare,
      color: 'primary'
    },
    {
      label: 'Leads Captured',
      value: leadsInRange,
      icon: Users,
      color: 'success'
    },
    {
      label: 'Customer Messages',
      value: inputMessages,
      icon: MessageCircle,
      color: 'amber'
    },
    {
      label: 'AI Responses',
      value: outputMessages,
      icon: Bot,
      color: 'primary'
    }
  ];

  // Get number of days to display based on selected range
  const getDaysCount = () => {
    switch (timeRange) {
      case '24h': return 1;
      case '7d': return 7;
      case '30d': return 30;
      case '90d': return 90;
      default: return 30;
    }
  };

  // Prepare chart data based on selected time range
  const getChartDays = () => {
    const daysCount = getDaysCount();
    const days = [];

    for (let i = daysCount - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      // Format label based on range
      let label;
      if (timeRange === '24h') {
        label = 'Today';
      } else if (timeRange === '7d') {
        label = date.toLocaleDateString('en-US', { weekday: 'short' });
      } else if (timeRange === '30d') {
        // Show day number for 30 days
        label = date.getDate().toString();
      } else {
        // Show week number or abbreviated date for 90 days
        label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }

      days.push({ date: dateStr, label });
    }
    return days;
  };

  const chartDays = getChartDays();
  const chartData = chartDays.map(({ date, label }) => ({
    label,
    date,
    conversations: conversationsByDay.find(c => c.date === date)?.count || 0,
    leads: leadsByDay.find(l => l.date === date)?.count || 0,
    messages: messagesByDay.find(m => m.date === date)?.count || 0
  }));

  // For 30d and 90d, aggregate into weekly buckets for better visualization
  const getDisplayData = () => {
    if (timeRange === '30d' || timeRange === '90d') {
      // Group by week
      const weeklyBuckets = [];
      const bucketSize = timeRange === '30d' ? 5 : 7; // 6 bars for 30d, ~13 bars for 90d

      for (let i = 0; i < chartData.length; i += bucketSize) {
        const bucket = chartData.slice(i, i + bucketSize);
        const startDate = new Date(bucket[0].date);
        const endDate = new Date(bucket[bucket.length - 1].date);

        weeklyBuckets.push({
          label: `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
          conversations: bucket.reduce((sum, d) => sum + d.conversations, 0),
          leads: bucket.reduce((sum, d) => sum + d.leads, 0),
          messages: bucket.reduce((sum, d) => sum + d.messages, 0)
        });
      }
      return weeklyBuckets;
    }
    return chartData;
  };

  const displayData = getDisplayData();

  // Find max value across all metrics for proper scaling
  const maxValue = Math.max(
    ...displayData.map(d => Math.max(d.conversations, d.leads, d.messages)),
    1
  );

  // Calculate period totals for the summary
  const periodTotals = {
    conversations: displayData.reduce((sum, d) => sum + d.conversations, 0),
    leads: displayData.reduce((sum, d) => sum + d.leads, 0),
    messages: displayData.reduce((sum, d) => sum + d.messages, 0)
  };

  return (
    <div className="p-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Analytics</h1>
            <p className="text-text-secondary">Performance insights for {agent?.name || 'your agent'}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchAnalytics}
            disabled={isLoading}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 text-text-muted ${isLoading ? 'animate-spin' : ''}`} />
          </button>

          {/* Time Range Selector */}
          <div className="flex items-center gap-2 bg-white rounded-xl border border-border p-1">
            {['24h', '7d', '30d', '90d'].map(range => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`
                  px-4 py-2 rounded-lg text-sm font-medium transition-all
                  ${timeRange === range
                    ? 'bg-gradient-to-r from-primary to-accent text-white'
                    : 'text-text-secondary hover:bg-background'
                  }
                `}
              >
                {range}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {metrics.map((metric, index) => {
          const Icon = metric.icon;
          const colorClasses = {
            primary: 'from-primary/20 to-primary/5 text-primary',
            success: 'from-success/20 to-success/5 text-success',
            accent: 'from-accent/20 to-accent/5 text-accent',
            amber: 'from-amber-500/20 to-amber-500/5 text-amber-600'
          };

          return (
            <div
              key={metric.label}
              className="card p-6 animate-slide-up"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[metric.color]}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <span className="text-xs text-text-muted bg-background px-2 py-1 rounded-md">{rangePeriod}</span>
              </div>
              <p className="text-3xl font-bold text-text-primary mb-1">{metric.value.toLocaleString()}</p>
              <p className="text-sm text-text-muted">{metric.label}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Activity Chart */}
        <div className="lg:col-span-2 card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-text-primary">Activity ({rangePeriod})</h2>
            <div className="flex items-center gap-4 text-sm flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary" />
                <span className="text-text-muted">Conversations</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-success" />
                <span className="text-text-muted">Leads</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span className="text-text-muted">Messages</span>
              </div>
            </div>
          </div>

          {/* Bar Chart */}
          <div className="h-64 flex items-end justify-between gap-1 sm:gap-2 overflow-x-auto pb-2">
            {displayData.map((data, index) => (
              <div key={index} className="flex-1 min-w-[40px] flex flex-col items-center gap-2">
                <div className="w-full flex gap-0.5 h-48 items-end justify-center">
                  {/* Conversations bar */}
                  <div
                    className="flex-1 max-w-3 bg-primary rounded-t-md transition-all hover:opacity-80 cursor-pointer"
                    style={{
                      height: `${(data.conversations / maxValue) * 100}%`,
                      minHeight: data.conversations > 0 ? '4px' : '0px'
                    }}
                    title={`${data.conversations} conversations`}
                  />
                  {/* Leads bar */}
                  <div
                    className="flex-1 max-w-3 bg-success rounded-t-md transition-all hover:opacity-80 cursor-pointer"
                    style={{
                      height: `${(data.leads / maxValue) * 100}%`,
                      minHeight: data.leads > 0 ? '4px' : '0px'
                    }}
                    title={`${data.leads} leads`}
                  />
                  {/* Messages bar */}
                  <div
                    className="flex-1 max-w-3 bg-amber-500 rounded-t-md transition-all hover:opacity-80 cursor-pointer"
                    style={{
                      height: `${(data.messages / maxValue) * 100}%`,
                      minHeight: data.messages > 0 ? '4px' : '0px'
                    }}
                    title={`${data.messages} messages`}
                  />
                </div>
                <span className="text-[10px] sm:text-xs text-text-muted font-medium whitespace-nowrap">{data.label}</span>
              </div>
            ))}
          </div>

          {/* Period totals summary */}
          {(periodTotals.conversations > 0 || periodTotals.leads > 0 || periodTotals.messages > 0) && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-lg font-semibold text-primary">
                    {periodTotals.conversations}
                  </p>
                  <p className="text-xs text-text-muted">Conversations</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-success">
                    {periodTotals.leads}
                  </p>
                  <p className="text-xs text-text-muted">Leads</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-amber-500">
                    {periodTotals.messages}
                  </p>
                  <p className="text-xs text-text-muted">Messages</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Top Topics - Coming Soon */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-text-primary mb-6">Top Conversation Topics</h2>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Construction className="w-12 h-12 text-text-muted mb-3" />
            <p className="text-text-secondary font-medium">Coming Soon</p>
            <p className="text-sm text-text-muted mt-1">Topic analysis will be available in a future update</p>
          </div>
        </div>
      </div>

      {/* Additional Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        {/* Conversion Funnel */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-text-primary">Conversion Rate</h2>
            </div>
            <span className="text-xs text-text-muted bg-background px-2 py-1 rounded-md">{rangePeriod}</span>
          </div>
          <div className="text-center py-4">
            <div className="relative inline-flex items-center justify-center">
              <svg className="w-32 h-32 transform -rotate-90">
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  className="stroke-background"
                  strokeWidth="12"
                  fill="none"
                />
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  className="stroke-primary"
                  strokeWidth="12"
                  fill="none"
                  strokeDasharray={`${conversionRate * 3.52} 352`}
                  strokeLinecap="round"
                />
              </svg>
              <span className="absolute text-3xl font-bold text-text-primary">
                {conversionRate}%
              </span>
            </div>
            <p className="text-sm text-text-muted mt-4">
              {leadsInRange} leads from {conversationsInRange} conversations
            </p>
          </div>
        </div>

        {/* Peak Hours - Coming Soon */}
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-accent" />
            <h2 className="text-lg font-semibold text-text-primary">Peak Activity</h2>
          </div>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Construction className="w-12 h-12 text-text-muted mb-3" />
            <p className="text-text-secondary font-medium">Coming Soon</p>
            <p className="text-sm text-text-muted mt-1">Peak activity tracking will be available soon</p>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-success" />
              <h2 className="text-lg font-semibold text-text-primary">Quick Stats</h2>
            </div>
            <span className="text-xs text-text-muted bg-background px-2 py-1 rounded-md">{rangePeriod}</span>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-text-secondary">Total Messages</span>
              <span className="font-semibold text-text-primary">{periodTotals.messages.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-text-secondary">Avg Messages/Day</span>
              <span className="font-semibold text-text-primary">
                {getDaysCount() > 0 ? Math.round(periodTotals.messages / getDaysCount()) : 0}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-text-secondary">Bot Uptime</span>
              <span className="font-semibold text-success">99.9%</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-text-secondary">Active Since</span>
              <span className="font-semibold text-text-primary">
                {agent?.created_at
                  ? new Date(agent.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : '-'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}